import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useToastStore } from '@/components_new/Toaster/store'
import { useConfig } from '@/composables/config'
import { useTr } from '@/composables/use-tr'
import { PWA_APP_CLIDS } from '@/constants/general'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'

import toastImage1 from './images/image-1.webp'
import toastImage2 from './images/image-2.webp'
import toastImage3 from './images/image-3.webp'

const PUSH_SUBSCRIPTION_PROMPT_CONFIG = {
    MIN_VISITS: 2,
    MAX_DISPLAY_COUNT: 100,
    DISPLAY_DELAY_HOURS: 24 * 7,
} as const

export const usePushSubscriptionStore = defineStore(
    'push_subscription',
    () => {
        const { VUE_APP_ONESIGNAL_APPID, VUE_APP_ONESIGNAL_SAFARI_WEB_ID } = useConfig()
        const tr = useTr()
        const toastStore = useToastStore()
        const layoutStore = useLayoutStore()
        const loggerStore = useLoggerStore()
        const appStore = useAppStore()

        const properEnv = computed(() => appStore.platform === 'desktop' && appStore.isPWA)

        const displayCount = ref(0)
        const lastDisplayTime = ref(0)
        const optedOut = ref(false)
        const sessions = ref(0)

        const shouldShowPrompt = computed(() => {
            if (typeof window === 'undefined') {
                return false
            }

            if (!properEnv.value) {
                return false
            }

            if (layoutStore.fullScreen) {
                return false
            }

            if (Notification.permission !== 'default') {
                return false
            }

            if ('serviceWorker' in navigator === false) {
                return false
            }

            // keep showing the toast for now
            // if (optedOut.value) {
            //     return false
            // }

            if (displayCount.value >= PUSH_SUBSCRIPTION_PROMPT_CONFIG.MAX_DISPLAY_COUNT) {
                return false
            }

            if (
                Date.now() - lastDisplayTime.value <
                PUSH_SUBSCRIPTION_PROMPT_CONFIG.DISPLAY_DELAY_HOURS * 60 * 60 * 1000
            ) {
                return false
            }

            return true
        })

        const toastConfig = computed(() => {
            return [
                {
                    image: toastImage1,
                    title: tr('toast-push-subscription.title1'),
                    description: tr('toast-push-subscription.description1'),
                    buttonText: tr('toast-push-subscription.button1'),
                },
                {
                    image: toastImage2,
                    title: tr('toast-push-subscription.title2'),
                    description: tr('toast-push-subscription.description2'),
                    buttonText: tr('toast-push-subscription.button2'),
                },
                {
                    image: toastImage3,
                    title: tr('toast-push-subscription.title3'),
                    description: tr('toast-push-subscription.description3'),
                    buttonText: tr('toast-push-subscription.button3'),
                },
            ][displayCount.value % 3]!
        })

        const showSubscriptionPrompt = (
            { considerMinVisits }: { considerMinVisits: boolean } = { considerMinVisits: false },
        ) => {
            if (considerMinVisits && sessions.value < PUSH_SUBSCRIPTION_PROMPT_CONFIG.MIN_VISITS) {
                return
            }

            if (!shouldShowPrompt.value) {
                return
            }

            toastStore.showToast({
                type: 'push-subscription-prompt',
                title: toastConfig.value.title,
                description: toastConfig.value.description,
                image: toastConfig.value.image,
                buttonText: toastConfig.value.buttonText,
                priority: 1,
                imageStyles: { height: '120rem' },
                onAccept: () => {
                    window.OneSignalDeferred.push(async (OneSignal) => {
                        const result: NotificationPermission = await OneSignal.Notifications.requestPermission()
                        if (result === 'granted') {
                            loggerStore.logEvent({
                                event: 'custom_event',
                                eventName: 'push_notification_granted',
                            })
                        } else if (result === 'denied') {
                            loggerStore.logEvent({
                                event: 'custom_event',
                                eventName: 'push_notification_denied',
                            })
                        }
                    })
                },
                onClose: () => {
                    optedOut.value = true
                },
                onShow: () => {
                    displayCount.value += 1
                    lastDisplayTime.value = Date.now()

                    loggerStore.logInterfaceEvents({
                        interface_target: 'push-subscription-prompt-toast',
                        eventName: 'interface_show',
                    })
                },
                isFullScreenAllowed: false,
            })
        }

        const init = () => {
            if (properEnv.value) {
                window.OneSignalDeferred.push(async (OneSignal) => {
                    await OneSignal.init({
                        appId: VUE_APP_ONESIGNAL_APPID,
                        safari_web_id: VUE_APP_ONESIGNAL_SAFARI_WEB_ID,
                        notifyButton: {
                            enable: false,
                        },
                        welcomeNotification: {
                            disable: true,
                        },
                        autoResubscribe: true,
                        allowLocalhostAsSecureOrigin: true,
                        serviceWorkerPath: `OneSignalSDKWorker.js`,
                        serviceWorkerParam: { scope: '/' },
                    })

                    OneSignal.User.addTags({
                        url_scope: `${location.origin + location.pathname}?clid=${PWA_APP_CLIDS.pwa}`,
                    })
                    if (appStore.auid) {
                        await OneSignal.login(appStore.auid)
                    }
                })

                sessions.value += 1
            }
        }

        return {
            init,
            showSubscriptionPrompt,
            displayCount,
            lastDisplayTime,
            optedOut,
            sessions,
            properEnv,
        }
    },
    {
        persist: {
            key: 'push_subscribe',
            pick: ['displayCount', 'lastDisplayTime', 'optedOut', 'sessions'],
        },
    },
)
